<script setup lang="ts">
/**
 * LegalContent — 법률 문서 본문(SSoT 단일 렌더).
 * legal/* 독립 페이지(SEO·직접링크)와 LegalModal(가입 인라인 동의)이 동일 본문을 공유한다 —
 * 두 곳 복제 금지. 본문 데이터는 #/data/legal(원본 md 4종과 양방향 동기화) 가 SSoT.
 *
 * 블록 타입(section·paragraph·list·table·note·checkbox)을 순회 렌더한다.
 * draft:true 문서는 상단에 "법무 검토 전 초안" 고지 배너를 표시한다.
 * showHeader: 독립 페이지는 제목 헤더 표시(true), 모달은 자체 헤더가 있어 본문만(false).
 */
import { FileText, ShieldCheck, AlertTriangle, Info, AlertCircle } from 'lucide-vue-next';
import { computed } from 'vue';

import type { LegalDoc } from '#/data/legal';

interface Props {
  /** 법률 문서(legal.ts SSoT). */
  doc: LegalDoc;
  /** 아이콘 종류(약관·동의=문서, 개인정보·처리방침=방패). */
  icon?: 'terms' | 'privacy';
  /** 페이지 제목 헤더 표시 여부(독립 페이지 true, 모달 false). */
  showHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  icon: 'terms',
  showHeader: true,
});

/** 동의 항목(checkbox 블록)을 가진 문서인지 — 본문은 읽기 전용이므로 실제 동의 안내를 띄운다. */
const hasConsentItems = computed(() => props.doc.body.some((b) => b.kind === 'checkbox'));

/**
 * 인라인 강조 파서 — `**...**` 를 강조 세그먼트로 분해한다.
 * v-html 없이 세그먼트 배열을 v-for 로 렌더해 XSS 를 원천 차단(법률 텍스트는 신뢰 SSoT 지만 안전 우선).
 */
interface TextSegment {
  text: string;
  bold: boolean;
}
function parseInline(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) segments.push({ text: text.slice(last, m.index), bold: false });
    segments.push({ text: m[1], bold: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), bold: false });
  return segments;
}
</script>

<template>
  <div>
    <header v-if="showHeader" class="mb-6 border-b border-line pb-[26px] pt-[30px]">
      <div class="mb-3 flex items-center gap-2 text-brand-muted">
        <FileText v-if="icon === 'terms'" class="h-5 w-5" :stroke-width="1.7" />
        <ShieldCheck v-else class="h-5 w-5" :stroke-width="1.7" />
      </div>
      <h1
        class="m-0 font-serif text-[22px] font-extrabold tracking-[-0.02em] text-brand md:text-[27px]"
      >
        {{ doc.title }}
      </h1>
    </header>

    <p class="mb-5 text-[12.5px] text-muted">{{ doc.meta }}</p>

    <!-- 초안 고지 배너(draft:true 문서) -->
    <div
      v-if="doc.draft"
      class="border-accent/40 bg-accent/10 mb-7 flex items-start gap-3 rounded-card border px-4 py-3.5"
      role="note"
    >
      <AlertTriangle class="mt-0.5 h-[18px] w-[18px] shrink-0 text-accent-deep" :stroke-width="2" />
      <div class="text-[12.5px] leading-[1.65] text-body">
        <span class="font-bold text-brand">법무 검토 전 초안입니다.</span>
        본 문서는 게시 전 변호사·법무 검토를 거쳐 확정될 예정이며, 일부 항목은 변경될 수 있습니다.
        궁금한 사항은 1:1 문의로 안내해 드립니다.
      </div>
    </div>

    <!-- 도입 머리말 -->
    <div v-if="doc.intro?.length" class="mb-6 space-y-3">
      <p
        v-for="(line, i) in doc.intro"
        :key="`intro-${i}`"
        class="text-[14px] leading-[1.75] text-body"
      >
        <template v-for="(seg, si) in parseInline(line)" :key="si">
          <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
          <template v-else>{{ seg.text }}</template>
        </template>
      </p>
    </div>

    <!-- 본문 블록 — 계층 간격 대비: 조 사이(mt-10) ≫ 조 내부 항·문단(mb-4) > 중첩 호(space-y-2) -->
    <div>
      <template v-for="(block, i) in doc.body" :key="i">
        <!-- 조 제목: 조 사이를 넓게 띄워 시각적 그룹으로 분리(mt-10), 제목↔본문은 가깝게(mb-3.5) -->
        <h2
          v-if="block.kind === 'section'"
          class="mb-3.5 mt-10 font-serif text-[16px] font-extrabold tracking-[-0.01em] text-brand first:mt-0 md:text-[17px]"
        >
          {{ block.title }}
        </h2>

        <!-- 문단 -->
        <p
          v-else-if="block.kind === 'paragraph'"
          class="mb-4 text-[14px] leading-[1.8] text-body last:mb-0"
        >
          <template v-for="(seg, si) in parseInline(block.text)" :key="si">
            <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
            <template v-else>{{ seg.text }}</template>
          </template>
        </p>

        <!-- 목록(번호/불릿, 1단계 중첩) — 항 사이는 넉넉히(space-y-3) 분리 -->
        <component
          :is="block.ordered ? 'ol' : 'ul'"
          v-else-if="block.kind === 'list'"
          class="mb-4 space-y-3 pl-1 text-[14px] leading-[1.8] text-body last:mb-0"
        >
          <li v-for="(item, j) in block.items" :key="j" class="grid grid-cols-[auto_1fr] gap-x-2">
            <span class="font-bold tabular-nums text-brand">
              {{ block.ordered ? `${j + 1}.` : '•' }}
            </span>
            <div>
              <span>
                <template v-for="(seg, si) in parseInline(item.text)" :key="si">
                  <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
                  <template v-else>{{ seg.text }}</template>
                </template>
              </span>
              <ol
                v-if="item.children?.length"
                class="mt-2 space-y-2 pl-1 text-[13.5px] leading-[1.7] text-muted"
              >
                <li
                  v-for="(child, k) in item.children"
                  :key="k"
                  class="grid grid-cols-[auto_1fr] gap-x-2"
                >
                  <span class="tabular-nums">{{ k + 1 }})</span>
                  <span>
                    <template v-for="(seg, si) in parseInline(child)" :key="si">
                      <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
                      <template v-else>{{ seg.text }}</template>
                    </template>
                  </span>
                </li>
              </ol>
            </div>
          </li>
        </component>

        <!-- 표 -->
        <div
          v-else-if="block.kind === 'table'"
          class="mb-4 overflow-x-auto rounded-card border border-line last:mb-0"
        >
          <table class="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr class="bg-bg-2">
                <th
                  v-for="(h, hi) in block.headers"
                  :key="hi"
                  class="border-b border-line px-3.5 py-2.5 font-bold text-brand"
                >
                  {{ h }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, ri) in block.rows"
                :key="ri"
                class="align-top [&:not(:last-child)]:border-b [&:not(:last-child)]:border-line"
              >
                <td
                  v-for="(cell, ci) in row"
                  :key="ci"
                  class="px-3.5 py-2.5 leading-[1.6] text-body"
                >
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 인용 노트(※) — 위 콘텐츠의 부연이라 살짝 붙이고(-mt-1), 노트끼리는 촘촘히(mb-1.5) -->
        <p
          v-else-if="block.kind === 'note'"
          class="-mt-1 mb-1.5 border-l-2 border-line pl-3 text-[12.5px] leading-[1.65] text-muted last:mb-0"
        >
          ※
          <template v-for="(seg, si) in parseInline(block.text)" :key="si">
            <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
            <template v-else>{{ seg.text }}</template>
          </template>
        </p>

        <!-- 강조 박스(callout) — 분쟁 소지 큰 핵심 고지(청약철회·환불 제한·선택 동의 등) -->
        <div
          v-else-if="block.kind === 'callout'"
          class="mb-4 flex items-start gap-3 rounded-card border px-4 py-3.5 last:mb-0"
          :class="
            block.tone === 'info'
              ? 'bg-brand-muted/[0.06] border-brand-line-2'
              : 'border-accent/40 bg-accent/10'
          "
          role="note"
        >
          <component
            :is="block.tone === 'info' ? Info : AlertCircle"
            class="mt-0.5 h-[18px] w-[18px] shrink-0"
            :class="block.tone === 'info' ? 'text-brand-muted' : 'text-accent-deep'"
            :stroke-width="2"
          />
          <div class="text-[13px] leading-[1.7] text-body">
            <p v-if="block.title" class="mb-1 font-bold text-brand">{{ block.title }}</p>
            <p class="m-0">
              <template v-for="(seg, si) in parseInline(block.text)" :key="si">
                <strong v-if="seg.bold" class="font-bold text-brand">{{ seg.text }}</strong>
                <template v-else>{{ seg.text }}</template>
              </template>
            </p>
          </div>
        </div>

        <!-- 동의 항목(읽기 전용 표시) — 실제 동의는 가입 화면에서 받는다.
             체크박스 위젯을 흉내내지 않고 "동의 항목" 라벨로 정적 표시임을 명확히 한다. -->
        <div
          v-else-if="block.kind === 'checkbox'"
          class="mb-2.5 flex items-start gap-3 rounded-[10px] border border-line bg-bg-2 px-4 py-3 text-[13.5px] leading-[1.55] last:mb-0"
        >
          <span
            class="mt-px shrink-0 rounded-[6px] bg-white px-2 py-0.5 text-[11px] font-bold"
            :class="block.optional ? 'text-muted' : 'text-brand'"
          >
            {{ block.optional ? '선택 동의' : '필수 동의' }}
          </span>
          <span class="text-body">{{ block.text }}</span>
        </div>
      </template>
    </div>

    <!-- 동의 항목 안내 — 본문의 동의 항목은 표시용이며 실제 동의는 가입 화면에서 받는다 -->
    <div
      v-if="hasConsentItems"
      class="mt-6 flex items-start gap-2 rounded-card border border-line bg-bg-2 px-4 py-3 text-[12.5px] leading-[1.6] text-muted"
    >
      <Info class="mt-0.5 h-[15px] w-[15px] shrink-0" :stroke-width="1.8" />
      <span>
        위 동의 항목은 내용 확인을 위한 표시입니다. 실제 동의는 회원가입 화면에서 진행됩니다.
      </span>
    </div>
  </div>
</template>
